import {gql} from '@apollo/client';
import {naturalSortByKey} from '@benchling/js-utils';
import {DocumentNode} from 'graphql';
import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {Permissions} from 'auth/PermissionConstants';
import {
  OligoAwareFolderItemType,
  SchemaInterfaceExtendingSchema,
  TagSchema,
} from 'constants/RegistryConstants';
import {readQuerySafe, writeQuery} from 'graphql-client/ApolloWrappers';
import {QueryResultType, useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {graphQLArrayToREST} from 'graphql-client/declarative-translations/external/translate';
import {mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema} from 'lookup-config/LookupConfigUtils';
import StarsAwareSchemaSelector from 'samples/StarsAwareSchemaSelector';
import {Schema} from 'schemas/SchemaConstants';
import {WithGetSchemaOwner, shouldSkipOwnerQuery} from 'schemas/SchemaOwnerUtil';
import {isSchemaInterface, isTagSchema} from 'schemas/SchemaPredicates';
import filterTagSchemas from 'tag-schema/filterTagSchemas';
import {toggleTagSchemaStar} from 'tag-schema/TagSchemaActions';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';

import {SchemaInterfaceSelectorQuery} from './__generated/SchemaInterfaceSelectorQuery';
import {TagSchemaSelectorOwnerQuery} from './__generated/TagSchemaSelectorOwnerQuery';
import {
  TagSchemaSelectorQuery,
  TagSchemaSelectorQuery_tagSchemas_TagSchemaNode as TagSchemaGQL,
} from './__generated/TagSchemaSelectorQuery';

export interface TagSchemaOrInterfaceSelectorBaseProps {
  allowNone?: boolean;
  /** If no `oligoAwareFolderItemTypes` are given, will show all tag schemas associated with the given registry. */
  oligoAwareFolderItemTypes?: Array<OligoAwareFolderItemType> | null;
  registryId?: string | null;
  requireTranslationLink?: boolean;
  requiredPermissions?: Array<keyof Permissions>;
  /** Indicates whether the selector itself is disabled */
  isDisabled?: boolean;
  /** A value to append to the schema name when it's disabled explaining why */
  disabledMessage?: string;
  /**
   * Lets you specify custom logic for determining if a tag schema should be visible in the list of options.
   * Note that this does not apply to schema interfaces.
   */
  tagSchemaFilterFn?: (schema: TagSchema) => boolean;
  showLoadingIndicator?: boolean;
  shouldDefaultIfSingleItem?: boolean;
  dataTestAttrs?: DataTestAttrs;
}

export interface TagSchemaOrInterfaceSelectorProps extends TagSchemaOrInterfaceSelectorBaseProps {
  allowInterfaces?: boolean;
  selectedTagSchemaOrInterfaceId?: string | null;
  /**
   * The full TagSchema or Interface object corresponding to value if we want to force the selector to show it even if
   * it is not a valid selection (either user doesn't have permission or it was deleted.
   */
  forceVisibleSelectedTagSchemaOrInterface?: TagSchema | SchemaInterfaceExtendingSchema | null;
  /** The TagSchema could be null if `allowNone` is true.  Otherwise, it should not be null. */
  onChange: (tagSchemaOrInterface: TagSchema | SchemaInterfaceExtendingSchema | null) => void;
  /** Function from tag schema to boolean specifying whether the tag schema should be disabled in the list. */
  isTagSchemaOrInterfaceDisabledFn?: (schema: TagSchema | SchemaInterfaceExtendingSchema) => boolean;
}

export interface ConnectedProps {
  starredSchemaIds: Array<string> | null;
  tagSchemasOrInterfaces: Array<TagSchema | SchemaInterfaceExtendingSchema> | null;
  getSchemaOwner: (schema: {id: string; name: string}) => string | undefined;
}

/**
 * This is a noop function that returns undefined. It is used as a value for getSchemaOwner when the schema owners query is still resolving.
 */
const GET_OWNER_NOOP = (): undefined => undefined;

/**
 * This component should only be used in tests. Please use QueryingTagSchemaOrInterfaceSelector instead, or directly useStarsAwareSchemaSelector.
 */
export function TagSchemaOrInterfaceSelector({
  allowNone = false,
  isDisabled = false,
  selectedTagSchemaOrInterfaceId,
  onChange,
  isTagSchemaOrInterfaceDisabledFn = () => false,
  disabledMessage = '',
  dataTestAttrs,
  starredSchemaIds,
  tagSchemasOrInterfaces,
  getSchemaOwner,
  shouldDefaultIfSingleItem,
}: TagSchemaOrInterfaceSelectorProps & ConnectedProps): JSX.Element {
  const tagSchemaChangeHandler = (value: Schema | null): void => {
    if (value == null) {
      assert(Boolean(allowNone), 'Selected null for schema but allowNone not set');
      onChange(null);
    } else if (isTagSchema(value)) {
      onChange(value);
    } else if (isSchemaInterface(value)) {
      onChange(value);
    } else {
      assert(false, 'Value is not a tag schema or null');
    }
  };
  return (
    <StarsAwareSchemaSelector
      isDisabled={isDisabled || tagSchemasOrInterfaces == null}
      isSchemaDisabledFn={isTagSchemaOrInterfaceDisabledFn}
      disabledMessage={disabledMessage}
      onChange={tagSchemaChangeHandler}
      schemas={tagSchemasOrInterfaces}
      selectedSchemaId={selectedTagSchemaOrInterfaceId}
      shouldShowNoSchemaOption={allowNone}
      starredSchemaIds={starredSchemaIds}
      onToggleStar={toggleTagSchemaStar}
      getSchemaOwner={getSchemaOwner}
      shouldDefaultIfSingleItem={shouldDefaultIfSingleItem}
      dataTestAttrs={dataTestAttrs}
    />
  );
}

export const TAG_SCHEMA_SELECTOR_QUERY: DocumentNode = gql`
  query TagSchemaSelectorQuery @skipAudit @engComponent(name: SCHEMAS) {
    ...AllCompleteTagSchemasGraphQL
    ...StarredTagSchemaIdsGraphQL
  }
  ${getFragmentWrapper('AllCompleteTagSchemas').fragment}
  ${getFragmentWrapper('StarredTagSchemaIds').fragment}
`;

export function addTagSchemaToTagSchemaSelectorQueryCache(tagSchema: TagSchemaGQL): void {
  const tagSchemasQuery = readQuerySafe<TagSchemaSelectorQuery>({query: TAG_SCHEMA_SELECTOR_QUERY});

  if (!tagSchemasQuery) {
    return;
  }

  const newData = {
    ...tagSchemasQuery,
    tagSchemas: naturalSortByKey([...tagSchemasQuery.tagSchemas, tagSchema], 'name'),
  };
  writeQuery<TagSchemaSelectorQuery>({query: TAG_SCHEMA_SELECTOR_QUERY}, newData);
}

export const TAG_SCHEMA_SELECTOR_OWNER_INFO_QUERY: DocumentNode = gql`
  query TagSchemaSelectorOwnerQuery @skipAudit @engComponent(name: SCHEMAS) {
    tagSchemas {
      id
      registry {
        id
        organization {
          id
          name
        }
      }
    }
  }
`;

export const SCHEMA_INTERFACE_SELECTOR_QUERY: DocumentNode = gql`
  query SchemaInterfaceSelectorQuery @skipAudit @engComponent(name: SCHEMAS) {
    ...AllSchemaInterfacesGraphQL
  }
  ${getFragmentWrapper('AllSchemaInterfaces').fragment}
`;

function enforceSelectedTagSchemaOrInterfaceIsIncluded(
  forceVisibleSelectedTagSchemaOrInterface: TagSchema | SchemaInterfaceExtendingSchema | null | undefined,
  filtered: Array<TagSchema | SchemaInterfaceExtendingSchema>
): Array<TagSchema | SchemaInterfaceExtendingSchema> {
  if (forceVisibleSelectedTagSchemaOrInterface == null) {
    return filtered;
  } else {
    return filtered.every((ts) => ts.id !== forceVisibleSelectedTagSchemaOrInterface.id)
      ? [...filtered, forceVisibleSelectedTagSchemaOrInterface]
      : filtered;
  }
}

export default function QueryingTagSchemaOrInterfaceSelector(
  props: TagSchemaOrInterfaceSelectorProps
): JSX.Element {
  if (props.forceVisibleSelectedTagSchemaOrInterface && props.selectedTagSchemaOrInterfaceId) {
    assertAndContinue(
      props.forceVisibleSelectedTagSchemaOrInterface.id === props.selectedTagSchemaOrInterfaceId,
      `selectedTagSchemaId (${props.selectedTagSchemaOrInterfaceId}) and the ID of forceVisibleSelectedTagSchema (${props.forceVisibleSelectedTagSchemaOrInterface.id}) must match`
    );
  }

  const queryResponse = useSkippableBenchlingQuery<TagSchemaSelectorQuery>(TAG_SCHEMA_SELECTOR_QUERY);
  const interfaceQueryResponse = useSkippableBenchlingQuery<SchemaInterfaceSelectorQuery>(
    SCHEMA_INTERFACE_SELECTOR_QUERY,
    {skip: !props.allowInterfaces}
  );

  const schemaOwnerResponse = useSkippableBenchlingQuery<TagSchemaSelectorOwnerQuery>(
    TAG_SCHEMA_SELECTOR_OWNER_INFO_QUERY,
    {skip: shouldSkipOwnerQuery()}
  );

  if (
    props.showLoadingIndicator &&
    (queryResponse.type === QueryResultType.PENDING ||
      interfaceQueryResponse.type === QueryResultType.PENDING)
  ) {
    return <CenteredHorizontalThrobber />;
  }

  if (
    queryResponse.type !== QueryResultType.SUCCESS ||
    !(
      interfaceQueryResponse.type === QueryResultType.SUCCESS ||
      interfaceQueryResponse.type === QueryResultType.SKIPPED
    )
  ) {
    return (
      <TagSchemaOrInterfaceSelector
        {...props}
        tagSchemasOrInterfaces={null}
        starredSchemaIds={null}
        getSchemaOwner={GET_OWNER_NOOP}
      />
    );
  }

  return (
    <WithGetSchemaOwner<TagSchemaSelectorOwnerQuery>
      result={schemaOwnerResponse}
      getIdOwnerTuples={(data) =>
        data.tagSchemas.map((schema: {id: string; registry: {organization: {name: string}}}) => ({
          id: schema.id,
          owner: schema.registry.organization.name,
        }))
      }
    >
      {({getSchemaOwner}) => (
        <TagSchemaOrInterfaceSelector
          {...props}
          getSchemaOwner={getSchemaOwner}
          tagSchemasOrInterfaces={enforceSelectedTagSchemaOrInterfaceIsIncluded(
            props.forceVisibleSelectedTagSchemaOrInterface,
            [
              ...filterTagSchemas(graphQLArrayToREST('TagSchema', queryResponse.data.tagSchemas), {
                registryId: props.registryId,
                oligoAwareFolderItemTypes: props.oligoAwareFolderItemTypes,
                requireTranslationLink: props.requireTranslationLink,
                requiredPermissions: props.requiredPermissions,
                customFilter: props.tagSchemaFilterFn,
                // Since archived tag schemas get filtered out, we ensure that the currently-selected schema
                // will always be included in the list of options, even if it's archived.
                ...(props.selectedTagSchemaOrInterfaceId
                  ? {alwaysIncludeId: props.selectedTagSchemaOrInterfaceId}
                  : {}),
              }),
              ...(interfaceQueryResponse.data === undefined
                ? []
                : interfaceQueryResponse.data.schemaInterfaces.map((schemaInterface) =>
                    mapSchemaInterfaceGraphQLToSchemaInterfaceExtendingSchema(schemaInterface)
                  )),
            ]
          )}
          starredSchemaIds={queryResponse.data.starredTagSchemas.map((item) => item.id)}
        />
      )}
    </WithGetSchemaOwner>
  );
}
